/**
 * Konva ↔ model geometry sync and **internal** rounding rules for cm / degrees.
 * Not exported from `@unpispas/upp-konva` — consumers rely on values in **`model.area`** / **`UppKonvaShapeHandle`** after the host applies sync.
 */
import type { UppFloorPlanElement } from '../plan/floor-plan-element';
import { UppFloorPlanVertex, UppFloorPlanTable } from '../plan/floor-plan-element';

/** @internal Decimal places for cm fields written from the canvas (avoids long float tails from px→cm). */
export const UPP_FLOOR_PLAN_GEOMETRY_CM_DECIMALS = 2;

/** @internal Coerces DTO/wire geometry to a finite number (`NaN` / `±Infinity` → `fallback`). */
export function floorPlanFiniteNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** @internal Like {@link floorPlanFiniteNumber} but enforces a strictly positive minimum (room fit, zoom, etc.). */
export function floorPlanFinitePositive(value: unknown, minPositive: number): number {
  const n = floorPlanFiniteNumber(value, minPositive);
  return n > 0 ? n : minPositive;
}

/** @internal Decimal places for rotation written from the canvas. */
export const UPP_FLOOR_PLAN_GEOMETRY_DEG_DECIMALS = 1;

function roundToDecimals(value: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(value * f) / f;
}

/** @internal */
export function roundFloorPlanGeometryCm(value: number): number {
  return roundToDecimals(value, UPP_FLOOR_PLAN_GEOMETRY_CM_DECIMALS);
}

/** @internal */
export function roundFloorPlanGeometryDeg(value: number): number {
  return roundToDecimals(value, UPP_FLOOR_PLAN_GEOMETRY_DEG_DECIMALS);
}

/** Konva rotation can be negative or >360°; plan contract is **\[0, 360)** degrees. */
export function normalizePlanRotationDeg(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  let x = value % 360;
  if (x < 0) {
    x += 360;
  }
  return x === 0 ? 0 : x;
}

/** Normalizes to \[0, 360) then applies {@link roundFloorPlanGeometryDeg}. */
export function roundNormalizedPlanRotationDeg(value: number): number {
  return roundFloorPlanGeometryDeg(normalizePlanRotationDeg(value));
}

export function isFloorPlanElementTransformable(el: UppFloorPlanElement): boolean {
  return !(el instanceof UppFloorPlanVertex);
}

/**
 * True when Konva places the transform origin at the bbox centre (table + chairs group in `table.ts`).
 * For those nodes, `UppFloorPlanElement` xCm/yCm are **unrotated top-left** in room space; derive them from
 * centre minus half size, not from `getTransform().point(0,0)` (which follows the rotated local corner).
 */
function isTransformOriginAtBBoxCenter(node: any, widthPx: number, heightPx: number): boolean {
  if (widthPx <= 0 || heightPx <= 0) {
    return false;
  }
  const ox = typeof node.offsetX === 'function' ? node.offsetX() : 0;
  const oy = typeof node.offsetY === 'function' ? node.offsetY() : 0;
  const halfW = widthPx / 2;
  const halfH = heightPx / 2;
  const eps = 0.5;
  return Math.abs(ox - halfW) < eps && Math.abs(oy - halfH) < eps;
}

function shouldSyncTopLeftFromBBoxCentre(
  el: UppFloorPlanElement,
  node: any,
  widthPx: number,
  heightPx: number,
): boolean {
  if (!(el instanceof UppFloorPlanTable) || !isTransformOriginAtBBoxCenter(node, widthPx, heightPx)) {
    return false;
  }
  /** Ellipse tables (with or without chairs) and rectangular tables with chairs use the centre-offset group. */
  return el.tableShape === 'ELLIPSE' || el.chairCount > 0;
}

function syncTopLeftCmFromNode(
  node: any,
  el: UppFloorPlanElement,
  scalePxPerCm: number,
  widthPx: number,
  heightPx: number,
): void {
  const wCm = widthPx / scalePxPerCm;
  const hCm = heightPx / scalePxPerCm;
  if (shouldSyncTopLeftFromBBoxCentre(el, node, widthPx, heightPx)) {
    const cx = typeof node.x === 'function' ? node.x() : 0;
    const cy = typeof node.y === 'function' ? node.y() : 0;
    el.xCm = roundFloorPlanGeometryCm(cx / scalePxPerCm - wCm / 2);
    el.yCm = roundFloorPlanGeometryCm(cy / scalePxPerCm - hCm / 2);
    return;
  }
  const p = node.getTransform().point({ x: 0, y: 0 });
  el.xCm = roundFloorPlanGeometryCm(p.x / scalePxPerCm);
  el.yCm = roundFloorPlanGeometryCm(p.y / scalePxPerCm);
}

/**
 * Writes Konva node geometry (after drag or transform) into the live model element (cm, degrees).
 * Expects nodes created by `createFloorPlanElementNode`: top-left local origin at (0,0), or table-with-chairs
 * group with `offsetX`/`offsetY` at half size (rotation around bbox center).
 */
export function syncKonvaNodeGeometryToElement(
  node: any,
  el: UppFloorPlanElement,
  scalePxPerCm: number,
  mode: 'move' | 'resizeOrRotate',
): void {
  if (mode === 'move') {
    if (el instanceof UppFloorPlanVertex) {
      const cxPx = typeof node.x === 'function' ? node.x() : 0;
      const cyPx = typeof node.y === 'function' ? node.y() : 0;
      const cxCm = cxPx / scalePxPerCm;
      const cyCm = cyPx / scalePxPerCm;
      el.xCm = roundFloorPlanGeometryCm(cxCm - el.widthCm / 2);
      el.yCm = roundFloorPlanGeometryCm(cyCm - el.heightCm / 2);
      return;
    }
    const wPx = typeof node.width === 'function' ? node.width() : 0;
    const hPx = typeof node.height === 'function' ? node.height() : 0;
    syncTopLeftCmFromNode(node, el, scalePxPerCm, wPx, hPx);
    return;
  }

  const sx = node.scaleX();
  const sy = node.scaleY();
  let wPx = node.width() * sx;
  let hPx = node.height() * sy;
  wPx = Math.max(1, wPx);
  hPx = Math.max(1, hPx);

  node.width(wPx);
  node.height(hPx);
  node.scaleX(1);
  node.scaleY(1);

  const ox = typeof node.offsetX === 'function' ? node.offsetX() : 0;
  const oy = typeof node.offsetY === 'function' ? node.offsetY() : 0;
  if (ox !== 0 || oy !== 0) {
    node.offsetX(wPx / 2);
    node.offsetY(hPx / 2);
  }

  syncTopLeftCmFromNode(node, el, scalePxPerCm, wPx, hPx);
  el.widthCm = roundFloorPlanGeometryCm(wPx / scalePxPerCm);
  el.heightCm = roundFloorPlanGeometryCm(hPx / scalePxPerCm);
  el.rotationDeg = roundNormalizedPlanRotationDeg(node.rotation());
}
